import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ManualPaymentMethod } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export type PaymentMethodAction = 'edit' | 'toggle' | 'delete';

export interface PaymentMethodsTableProps {
  methods: readonly ManualPaymentMethod[];
  /** Shows the switch and row menu; false for read-only roles and closed stores. */
  editable: boolean;
  onAction: (action: PaymentMethodAction, method: ManualPaymentMethod) => void;
  busyId?: string | null;
}

function Limits({ method }: { method: ManualPaymentMethod }) {
  const { t } = useTranslation('paymentMethods');
  const format = useFormat();
  if (method.minOrder && method.maxOrder) {
    return t('limits.range', {
      min: format.money(method.minOrder),
      max: format.money(method.maxOrder),
    });
  }
  if (method.minOrder) return t('limits.min', { min: format.money(method.minOrder) });
  if (method.maxOrder) return t('limits.max', { max: format.money(method.maxOrder) });
  return <span className="text-muted-foreground">{t('noLimits')}</span>;
}

/** One row per method in display order; the switch flips `isActive` in place. */
export function PaymentMethodsTable({
  methods,
  editable: canManage,
  onAction,
  busyId,
}: PaymentMethodsTableProps) {
  const { t } = useTranslation('paymentMethods');
  const format = useFormat();

  return (
    <div
      className="overflow-hidden rounded-lg border"
      data-tour="settings.payment-methods.manual-table"
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12 text-center">{t('columns.position')}</TableHead>
            <TableHead>{t('columns.method')}</TableHead>
            <TableHead className="text-right">{t('columns.fee')}</TableHead>
            <TableHead>{t('columns.limits')}</TableHead>
            <TableHead>{t('columns.active')}</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">{t('columns.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {methods.map((method) => (
            <TableRow
              key={method.id}
              className={cn(!method.isActive && 'text-muted-foreground')}
              aria-label={method.name}
            >
              <TableCell className="text-center tabular-nums">{method.position}</TableCell>
              <TableCell>
                <div className="flex flex-col gap-0.5">
                  <span className="text-foreground font-medium">{method.name}</span>
                  <span className="flex flex-wrap items-center gap-1.5 text-xs">
                    <code>{method.key}</code>
                    {method.description ? (
                      <span className="text-muted-foreground">· {method.description}</span>
                    ) : null}
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {method.fee.amount === 0 ? (
                  <span className="text-muted-foreground">{t('noFee')}</span>
                ) : (
                  format.money(method.fee)
                )}
              </TableCell>
              <TableCell className="text-sm">
                <Limits method={method} />
              </TableCell>
              <TableCell>
                {canManage ? (
                  <Switch
                    checked={method.isActive}
                    disabled={busyId === method.id}
                    aria-label={t('actions.toggle', { name: method.name })}
                    onCheckedChange={() => onAction('toggle', method)}
                  />
                ) : (
                  <Badge variant="outline">
                    {method.isActive ? t('status.active') : t('status.inactive')}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                {canManage ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('actions.menu', { name: method.name })}
                      >
                        <MoreHorizontal aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onSelect={() => onAction('edit', method)}>
                        <Pencil aria-hidden />
                        {t('actions.edit')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() => onAction('delete', method)}
                      >
                        <Trash2 aria-hidden />
                        {t('actions.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
